import { supabase } from '@/lib/supabase/client';
import {
  deriveHandoffStatuses,
  mergeDealWonHandoffItems,
  resolveHandoffCompletedAt,
  type DealWonHandoffItemRow,
} from '@/lib/customer-ops/deal-coordination';
import type {
  DealWonHandoffItem,
  DealWonHandoffItemUpdate,
  WonHandoffItemKey,
} from '@/types/deal-coordination';

export const dealWonHandoffService = {
  async listByDealId(
    dealId: string,
    context?: { locationId: string | null; csOwner: string | null }
  ): Promise<DealWonHandoffItem[]> {
    try {
      const { data, error } = await supabase
        .from('deal_won_handoff_items')
        .select('*')
        .eq('deal_id', dealId);

      if (error) {
        console.error('Error fetching deal won handoff items:', error);
        return mergeDealWonHandoffItems(dealId, []);
      }

      const merged = mergeDealWonHandoffItems(dealId, (data ?? []) as DealWonHandoffItemRow[]);

      if (!context) {
        return merged;
      }

      const derived = deriveHandoffStatuses(context);
      return merged.map((item) => {
        const derivedStatus = derived[item.item_key];
        if (!derivedStatus || item.status === 'complete' || item.status === 'blocked') {
          return item;
        }
        if (derivedStatus === 'complete' && item.status === 'not_started') {
          return { ...item, status: 'complete' as const };
        }
        return item;
      });
    } catch (error) {
      console.error('Error fetching deal won handoff items:', error);
      return mergeDealWonHandoffItems(dealId, []);
    }
  },

  async upsertItem(
    dealId: string,
    itemKey: WonHandoffItemKey,
    data: DealWonHandoffItemUpdate
  ): Promise<DealWonHandoffItem> {
    const existingRows = await this.listByDealId(dealId);
    const existing = existingRows.find((row) => row.item_key === itemKey);
    if (!existing) {
      throw new Error(`Unknown won handoff item: ${itemKey}`);
    }

    const nextStatus = data.status ?? existing.status;
    const payload = {
      deal_id: dealId,
      item_key: itemKey,
      status: nextStatus,
      notes: data.notes !== undefined ? data.notes : existing.notes,
      completed_at: resolveHandoffCompletedAt(nextStatus, existing.completed_at),
    };

    const { data: saved, error } = await supabase
      .from('deal_won_handoff_items')
      .upsert(payload, { onConflict: 'deal_id,item_key' })
      .select()
      .single();

    if (error) {
      console.error('Error upserting deal won handoff item:', error);
      throw new Error(`Failed to save handoff item ${itemKey}: ${error.message}`);
    }

    return mergeDealWonHandoffItems(dealId, [saved as DealWonHandoffItemRow]).find(
      (row) => row.item_key === itemKey
    )!;
  },

  async completeItem(dealId: string, itemKey: WonHandoffItemKey, notes?: string): Promise<DealWonHandoffItem> {
    return this.upsertItem(dealId, itemKey, { status: 'complete', notes });
  },
};
